import { useState } from 'react';
import { Button } from '@mantine/core';
import { useParams } from 'react-router-dom';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import PublicationPanel from '@/pages/Editor/components/Publication/PublicationPanel.tsx';
import PublicationHistory from '@/pages/Editor/components/Publication/PublicationHistory.tsx';
import SimplePublishReviewModal from '@/pages/Editor/components/Publication/SimplePublishReviewModal.tsx';
import CompositionWorkspace from '@/pages/Editor/components/Composition/CompositionWorkspace.tsx';
import type { AutomationVersionSimple } from '@/types/automations.ts';

const standardVersion: AutomationVersionSimple = {
  id: 'root-v1',
  name: 'Standard publication',
  version_number: 1,
  created_by: '',
  created_by_type: 'user',
  created_by_display_name: '',
  created_by_email: '',
  created_by_label: '',
  created_at: '',
  creation_kind: 'editor_edit',
  change_note: '',
  source_version_id: '',
  published_at: null,
  published_version_count: null,
  pinned_steps: [],
  pinned_outputs: [],
};

const publishedStandardVersion: AutomationVersionSimple = {
  ...standardVersion,
  id: 'root-v0',
  name: 'Published standard publication',
  version_number: 0,
  published_at: '2026-10-01T00:00:00Z',
  published_version_count: 1,
};

export default function E2EPublishReviewHarness() {
  const { automationId } = useParams();
  const [dialog, setDialog] = useState(false);
  const [guidedFlowOpened, setGuidedFlowOpened] = useState(false);
  if (automationId === 'standard' || automationId === 'standard-warning') {
    const warning = automationId === 'standard-warning';
    return (
      <SimplePublishReviewModal
        opened
        onClose={() => undefined}
        onOpenGuided={() => undefined}
        onOpenAdvanced={() => undefined}
        latestVersion={standardVersion}
        publishedVersion={warning ? publishedStandardVersion : null}
        summary={{
          automation_id: 'root',
          head_revision: 0,
          draft_version: { id: 'root-v1', number: 1 },
          published_version: warning ? { id: 'root-v0', number: 0 } : null,
          journey: 'direct_publish',
          state: 'ready_to_publish',
          next_action: 'publish_direct_version',
          comparison: warning
            ? { from_version_id: 'root-v0', to_version_id: 'root-v1' }
            : null,
          can_publish_direct_version: true,
          active_target_kind: warning ? 'version' : null,
          requires_advanced_release_configuration: false,
          reasons: [],
          publication_integrity: 'unpublished',
          test_status: {
            automation_id: 'root',
            automation_version_id: 'root-v1',
            suite_id: null,
            suite_digest: null,
            overall_status: warning ? 'attention_needed' : 'passing',
            total_configured_tests: warning ? 15 : 1,
            passed: 1,
            failed: 0,
            not_run: warning ? 14 : 0,
            running: 0,
            incomplete: 0,
            repair_needed: 0,
            detail_reference: null,
            message: null,
          },
          documentation: warning
            ? {
                event_id: null,
                revision_id: null,
                alignment_status: 'review_needed',
                reason_codes: ['review_not_recorded'],
              }
            : null,
        }}
      />
    );
  }
  if (automationId === 'guided') {
    return (
      <SimplePublishReviewModal
        opened
        onClose={() => undefined}
        onOpenGuided={() => undefined}
        onOpenAdvanced={() => undefined}
        latestVersion={standardVersion}
        publishedVersion={null}
        summary={{
          automation_id: 'root',
          head_revision: 0,
          draft_version: { id: 'root-v1', number: 1 },
          published_version: null,
          journey: 'guided_dependency_selection',
          state: 'advanced_configuration_required',
          next_action: 'open_advanced_release_configuration',
          comparison: null,
          can_publish_direct_version: false,
          active_target_kind: null,
          requires_advanced_release_configuration: true,
          reasons: [
            {
              code: 'composition_selection_required',
              message:
                'This automation needs a composition because one or more child implementations must be selected.',
            },
          ],
          publication_integrity: 'unpublished',
          test_status: null,
        }}
      />
    );
  }
  if (automationId === 'guided-flow') {
    return (
      <>
        {!guidedFlowOpened && (
          <>
            <Button onClick={() => setDialog(true)}>Publish v1</Button>
            <SimplePublishReviewModal
              opened={dialog}
              onClose={() => setDialog(false)}
              onOpenGuided={() => {
                setDialog(false);
                setGuidedFlowOpened(true);
              }}
              onOpenAdvanced={() => undefined}
              latestVersion={standardVersion}
              publishedVersion={null}
              summary={{
                automation_id: 'root',
                head_revision: 0,
                draft_version: { id: 'root-v1', number: 1 },
                published_version: null,
                journey: 'guided_dependency_selection',
                state: 'advanced_configuration_required',
                next_action: 'open_advanced_release_configuration',
                comparison: null,
                can_publish_direct_version: false,
                active_target_kind: null,
                requires_advanced_release_configuration: true,
                reasons: [
                  {
                    code: 'composition_selection_required',
                    message:
                      'This automation needs a composition because one or more child implementations must be selected.',
                  },
                ],
                publication_integrity: 'unpublished',
                test_status: null,
              }}
            />
          </>
        )}
        {guidedFlowOpened && <CompositionWorkspace rootVersionId="root-v1" mode="guided" canEdit />}
      </>
    );
  }
  if (automationId === 'direct-restore') {
    return <PublicationHistory automationId="root" canEdit />;
  }
  const panel = (
    <PublicationPanel
      releaseId="release-1"
      canEdit
      automationIds={['root']}
      action={automationId === 'rollback' ? 'rollback' : 'activate'}
    />
  );
  return (
    <>
      <Button variant="default" onClick={() => setDialog(true)}>
        Open publication dialog
      </Button>
      {dialog ? (
        <StyledModal
          opened
          onClose={() => setDialog(false)}
          title="Publication and exact compositions"
          size="xl"
        >
          {panel}
        </StyledModal>
      ) : (
        panel
      )}
    </>
  );
}
